import { useUpdateJobStatus } from '@/api/Jobs';
import { useAppCustomers } from '@/hooks';
import { useTranslate } from '@/translations';
import { JobData, JobStatus } from '@/types';
import { App, Card, Select, Typography } from 'antd';
import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  canTransitionJobStatus,
  getJobStatusSelectOptions,
  isTerminalJobStatus,
} from '../../utils/jobStatusWorkflow';
import { LogsTimeline } from './LogsTimeline';
import { JobItemsDataGrid } from './JobItemsDataGrid';
import { JobInformation } from './JobInformation';

interface JobDetailProps {
  job: JobData;
  jobStatus?: JobStatus[];
}

export const JobDetail: FC<JobDetailProps> = ({ job, jobStatus }) => {
  const { t } = useTranslate();
  const { message } = App.useApp();
  const { hasAdminAccess } = useAppCustomers();
  const { mutateAsync: updateJobStatusAsync, isPending: isUpdatingJobStatus } =
    useUpdateJobStatus();

  const [selectedStatusId, setSelectedStatusId] = useState<number>(
    job.jobStatusId,
  );

  useEffect(() => {
    setSelectedStatusId(job.jobStatusId);
  }, [job.jobId, job.jobStatusId]);

  const statusOptions = useMemo(
    () =>
      getJobStatusSelectOptions(job.jobStatusId, jobStatus, {
        pendingStatusId:
          selectedStatusId !== job.jobStatusId ? selectedStatusId : undefined,
      }),
    [job.jobStatusId, jobStatus, selectedStatusId],
  );

  const statusSelectDisabled =
    !hasAdminAccess ||
    isUpdatingJobStatus ||
    isTerminalJobStatus(job.jobStatusId);

  const onStatusChange = async (nextStatusId: number) => {
    if (!canTransitionJobStatus(job.jobStatusId, nextStatusId)) {
      message.open({
        type: 'warning',
        content: 'This status change is not allowed.',
      });
      return;
    }
    setSelectedStatusId(nextStatusId);
    const res = await updateJobStatusAsync({
      jobId: job.jobId,
      jobStatusId: nextStatusId,
    });
    const success = !!res.data;
    if (!success) {
      setSelectedStatusId(job.jobStatusId);
    }
    message.open({
      type: success ? 'success' : 'error',
      content: success
        ? 'Job status updated successfully!'
        : `Failed to update job status: ${res.message?.Message || ''}`,
    });
  };

  return (
    <div className="job-detail-panel d-flex flex-column gap-3">
      <Card className="job-detail-panel__card">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <Typography.Title level={4} className="m-0">
            Job ID: {job.jobId}
          </Typography.Title>
          <div className="d-flex align-items-center gap-2">
            <Typography.Text strong>{t('status')}:</Typography.Text>
            <Select
              value={selectedStatusId}
              options={statusOptions}
              className="job-detail-panel__status-select"
              disabled={statusSelectDisabled}
              loading={isUpdatingJobStatus}
              onChange={onStatusChange}
            />
          </div>
        </div>
        <JobInformation job={job} />
      </Card>

      <Card title="Items" className="job-detail-panel__card">
        <JobItemsDataGrid
          jobId={job.jobId}
          items={job.items || []}
          jobStatus={jobStatus}
        />
      </Card>

      <Card title="Logs" className="job-detail-panel__card">
        <LogsTimeline logs={job.logs || []} />
      </Card>
    </div>
  );
};
